import { Drawer } from 'antd';
import { FC, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { NavLink } from 'react-router-dom';

import { useTranslation } from '~shared/lib/i18n';
import { exitWebview } from '~shared/lib/mobile-auth';
import { RoutesUrls } from '~shared/lib/router';
import {
  FeedbackIcon,
  HelpIcon,
  LogoutIcon,
  MenuIcon,
  ParentsMembers,
  ParentsVoucherRoute,
  PersonIcon,
  SettingsIcon,
} from '~shared/ui';

import styles from './mobileAuthHeader.module.scss';

const BackIcon: FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15 18L9 12L15 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BrandLogo: FC = () => (
  <svg width="28" height="28" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.5188 26.2242L13.8984 20.1769L12.278 26.2242C11.6263 28.6567 8.36533 29.0994 7.0886 26.9288C5.74691 24.6477 3.84566 22.7465 1.56459 21.4048C-0.606028 20.1281 -0.163297 16.8671 2.26916 16.2154L8.31656 14.595L2.26916 12.9746C-0.163293 12.3228 -0.606028 9.06186 1.5646 7.78514C3.84567 6.44343 5.74691 4.54219 7.0886 2.26112C8.36533 0.0905043 11.6263 0.533239 12.278 2.9657L13.8984 9.01305L15.5188 2.9657C16.1706 0.533239 19.4315 0.0905043 20.7082 2.26114C22.0499 4.54221 23.9512 6.44345 26.2322 7.78514C28.4029 9.06188 27.9601 12.3228 25.5277 12.9746L19.4803 14.595L25.5277 16.2154C27.9601 16.8671 28.4029 20.1281 26.2322 21.4048C23.9512 22.7465 22.0499 24.6477 20.7082 26.9288C19.4315 29.0994 16.1706 28.6567 15.5188 26.2242Z"
      fill="currentColor"
    />
  </svg>
);

export interface MobileAuthHeaderProps {}

export const MobileAuthHeader: FC<MobileAuthHeaderProps> = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  let pageKey: string | undefined;

  for (const [key, path] of Object.entries(RoutesUrls)) {
    if (matchPath(path, location.pathname)) {
      pageKey = key;
      break;
    }
  }

  const drawerItems = [
    {
      path: RoutesUrls.parentCabinetPersonal,
      title: t('cm:pages.parentCabinetPersonal') as string,
      icon: <PersonIcon />,
    },
    {
      path: RoutesUrls.parentCabinetMembers,
      title: t('cm:pages.parentCabinetMembers') as string,
      icon: <ParentsMembers />,
    },
    {
      path: RoutesUrls.parentCabinetVoucher,
      title: t('cm:pages.parentCabinetVoucher') as string,
      icon: <ParentsVoucherRoute />,
    },
    {
      path: RoutesUrls.settings,
      title: t('cm:pages.settings') as string,
      icon: <SettingsIcon />,
    },
    {
      path: RoutesUrls.parentHelp,
      title: t('cm:pages.parentHelp') as string,
      icon: <HelpIcon />,
    },
    {
      path: RoutesUrls.feedback,
      title: t('cm:pages.feedback') as string,
      icon: <FeedbackIcon />,
    },
  ];

  const closeDrawer = () => setMenuOpen(false);

  return (
    <div className={styles.wrapper}>
      <button type="button" className={styles.iconBtn} onClick={exitWebview} aria-label="back">
        <BackIcon />
      </button>

      <div className={styles.titleGroup}>
        <span className={styles.brand} aria-hidden>
          <BrandLogo />
        </span>
        <p className={styles.title}>{pageKey ? (t(`cm:pages.${pageKey}`) as string) : ''}</p>
      </div>

      <button
        type="button"
        className={styles.iconBtn}
        onClick={() => setMenuOpen(true)}
        aria-label="menu"
      >
        <MenuIcon />
      </button>

      <Drawer
        placement="right"
        open={menuOpen}
        onClose={closeDrawer}
        width={280}
        closable
        rootClassName={styles.drawerRoot}
      >
        <div className={styles.drawerList}>
          {drawerItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeDrawer}
              className={({ isActive }) =>
                `${styles.drawerItem} ${isActive ? styles.drawerItemActive : ''}`
              }
            >
              <span className={styles.drawerIcon}>{item.icon}</span>
              <span>{item.title}</span>
            </NavLink>
          ))}

          <button
            type="button"
            onClick={() => {
              closeDrawer();
              exitWebview();
            }}
            className={`${styles.drawerItem} ${styles.drawerItemLogout}`}
          >
            <span className={styles.drawerIcon}>
              <LogoutIcon />
            </span>
            <span>{t('cm:pages.logout') as string}</span>
          </button>
        </div>
      </Drawer>
    </div>
  );
};
